import { DayRangePicker, type DateRange } from '@natera/material/lib/date'
import { addDays, format, parse, subDays } from 'date-fns'
import { useEffect, useState } from 'react'
import { cn } from '~/shared/lib/cn'
import { formatDateDefault } from '~/shared/lib/datetime'
import { Textfield } from '~/shared/ui/textfield'
import './date-range-picker.scss'

interface Range {
  from: string
  to: string
}

interface DateRangePickerProps {
  className?: string
  value: Range | undefined
  onChange: (dateRange: Range | undefined) => void
  maxDays?: number
}

export function DateRangePicker({ className, value, onChange, maxDays }: DateRangePickerProps) {
  const [range, setRange] = useState<DateRange | undefined>({
    from: value?.from ? parse(value.from, 'yyyy-MM-dd', new Date()) : undefined,
    to: value?.to ? parse(value.to, 'yyyy-MM-dd', new Date()) : undefined,
  })
  const [minDate, setMinDate] = useState<Date | undefined>(undefined)
  const [maxDate, setMaxDate] = useState<Date | undefined>(undefined)

  useEffect(() => {
    if (value?.from && value?.to) {
      setRange((range) => {
        const dateFrom = range?.from ? format(range.from, 'yyyy-MM-dd') : ''
        const dateTo = range?.to ? format(range.to, 'yyyy-MM-dd') : ''
        if (dateFrom === value?.from && dateTo === value?.to) {
          return range
        }
        return {
          from: value?.from ? parse(value.from, 'yyyy-MM-dd', new Date()) : undefined,
          to: value?.to ? parse(value.to, 'yyyy-MM-dd', new Date()) : undefined,
        }
      })
    }
  }, [value?.from, value?.to])

  useEffect(() => {
    if (range?.from && range?.to) {
      const dateFrom = format(range.from, 'yyyy-MM-dd')
      const dateTo = format(range.to, 'yyyy-MM-dd')
      if (dateFrom === value?.from && dateTo === value?.to) {
        return
      }
      onChange({
        from: format(range.from, 'yyyy-MM-dd'),
        to: format(range.to, 'yyyy-MM-dd'),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range])

  useEffect(() => {
    if (range?.from && !range?.to && maxDays) {
      const minDate = subDays(range.from, maxDays - 1)
      setMinDate(minDate)
      const maxDate = addDays(range.from, maxDays - 1)
      setMaxDate(maxDate)
    } else {
      setMinDate(undefined)
      setMaxDate(undefined)
    }
  }, [range, maxDays])

  return (
    <div className={cn('c-date-range-picker flex flex-col items-center gap-2', className)}>
      <Textfield
        className="w-full"
        placeholder="from"
        readOnly
        value={range?.from ? formatDateDefault(range.from) : ''}
      />
      <Textfield
        className="mb-2 w-full"
        placeholder="to"
        readOnly
        value={range?.to ? formatDateDefault(range.to) : ''}
      />
      <DayRangePicker
        numberOfMonths={1}
        materialArrowIcon="chevron_right"
        materialDropdownIcon="arrow_drop_down"
        range={range}
        onDateRangeChange={setRange}
        minDate={minDate}
        maxDate={maxDate}
      />
    </div>
  )
}
